import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { penaltyRulesAPI, branchesAPI, shiftsAPI, QUERY_KEYS } from '@/api';
import { Card, CardContent, CardHeader, CardTitle, Button, Input, Badge, Modal } from '@/components/ui';
import { useAuth } from '@/context/AuthContext';
import { BranchFilterSelect, LoadingSpinner, Pagination } from '@/components/common';
import { Scale, Plus, Edit2, Trash2, Sparkles, Clock, AlertTriangle, MapPinned, HelpCircle, Timer, LogOut, UserX, Ban } from 'lucide-react';
import { extractPaginatedData } from '@/utils/pagination';

const FILTER_SELECT_CLASS = 'min-h-[2.5rem] w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2 text-[13px] text-slate-700 shadow-sm outline-none transition focus:border-blue-400 focus:ring-2 focus:ring-blue-500/15';

const LATE_TYPES = [
  { value: 'fixed', label: 'Фиксированная сумма (одинаковая за каждое опоздание)' },
  { value: 'per_minute', label: 'За каждую минуту (минуты опоздания × сумма)' },
];

const toDisplayNumber = (value: any) => {
  if (value === null || typeof value === 'undefined' || value === '') return '-';
  const num = Number(value);
  if (Number.isNaN(num)) return '-';
  return num.toLocaleString();
};

function RuleMetricCard({
  icon,
  label,
  value,
  helper,
  iconClassName,
}: {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  helper?: string;
  iconClassName: string;
}) {
  return (
    <Card className="border-slate-200/90 bg-white/95">
      <CardContent className="flex items-center gap-3 p-4">
        <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${iconClassName}`}>
          {icon}
        </div>
        <div className="min-w-0">
          <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">{label}</p>
          <p className="mt-1 text-xl font-bold tracking-tight text-slate-900 sm:text-[1.4rem]">{value}</p>
          {helper && <p className="mt-1 text-[11px] leading-4 text-slate-500">{helper}</p>}
        </div>
      </CardContent>
    </Card>
  );
}

function RuleListCard({
  rule,
  lateTypeLabel,
  canManage,
  onEdit,
  onDelete,
  onPreview,
}: {
  rule: any;
  lateTypeLabel: string;
  canManage: boolean;
  onEdit: (rule: any) => void;
  onDelete: (id: number) => void;
  onPreview: (title: string, lines: Array<string | number | null | undefined>) => void;
}) {
  return (
    <Card className="overflow-hidden border-slate-200/90 bg-white/95 shadow-[0_18px_40px_-30px_rgba(15,23,42,0.35)]">
      <CardContent className="space-y-4 p-4 sm:p-5">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant={rule.is_active ? 'success' : 'secondary'}>
                {rule.is_active ? 'Активно' : 'Неактивно'}
              </Badge>
              <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600">
                Приоритет: {rule.priority ?? 0}
              </span>
            </div>
            <h3 className="mt-3 text-lg font-semibold tracking-tight text-slate-900">{rule.name}</h3>
            <p className="mt-2 text-[13px] leading-5 text-slate-600">
              {rule.description || 'К этому правилу описание не добавлено.'}
            </p>
          </div>

          {canManage && (
            <div className="flex items-center gap-2 self-start">
              <Button
                size="sm"
                variant="outline"
                className="h-9 w-9 min-w-[2.25rem] px-0"
                onClick={() => onEdit(rule)}
              >
                <Edit2 className="h-4 w-4" />
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="h-9 w-9 min-w-[2.25rem] px-0 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
                onClick={() => onDelete(rule.id)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>

        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-4">
            <div className="flex items-center gap-2 text-slate-700">
              <Clock className="h-4 w-4 text-amber-500" />
              <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">Опоздание</p>
            </div>
            <p className="mt-3 text-sm font-semibold text-slate-900">{lateTypeLabel}</p>
            <div className="mt-2 space-y-1 text-[13px] text-slate-600">
              <p>Сумма: {toDisplayNumber(rule.late_amount)}</p>
              <p>Порог: {toDisplayNumber(rule.late_threshold_minutes)} мин</p>
              {Array.isArray(rule.late_tiers) && rule.late_tiers.length > 0 && (
                <div className="mt-2 space-y-1 rounded-lg border border-amber-100 bg-amber-50/60 p-2">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-amber-700">Ступени</p>
                  {rule.late_tiers.map((tier: any, idx: number) => (
                    <p key={idx} className="text-[12px] text-amber-900">
                      {tier.from_minute}–{tier.to_minute} мин → {toDisplayNumber(tier.amount)}
                    </p>
                  ))}
                  <p className="text-[11px] text-amber-700">
                    Порог неявки: {toDisplayNumber(rule.no_show_after_minutes ?? rule.late_tiers[rule.late_tiers.length - 1]?.to_minute)} мин
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-4">
            <div className="flex items-center gap-2 text-slate-700">
              <AlertTriangle className="h-4 w-4 text-rose-500" />
              <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">Другие штрафы</p>
            </div>
            <div className="mt-3 space-y-1 text-[13px] text-slate-600">
              <p>Ранний уход: {toDisplayNumber(rule.early_leave_amount)}</p>
              <p>Порог раннего ухода: {toDisplayNumber(rule.early_leave_threshold_minutes)} мин</p>
              {Array.isArray(rule.early_leave_tiers) && rule.early_leave_tiers.length > 0 && (
                <div className="mt-2 space-y-1 rounded-lg border border-rose-100 bg-rose-50/60 p-2">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-rose-700">Ступени раннего ухода</p>
                  {rule.early_leave_tiers.map((tier: any, idx: number) => (
                    <p key={idx} className="text-[12px] text-rose-900">
                      {tier.from_minute}–{tier.to_minute} мин → {toDisplayNumber(tier.amount)}
                    </p>
                  ))}
                </div>
              )}
              <p>Неявка: {toDisplayNumber(rule.no_show_amount)}</p>
              <p>Отсутствие: {toDisplayNumber(rule.absence_amount)}</p>
              <p>Нет выхода: {toDisplayNumber(rule.no_checkout_amount)}</p>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-4 md:col-span-2 xl:col-span-1">
            <div className="flex items-center gap-2 text-slate-700">
              <MapPinned className="h-4 w-4 text-violet-500" />
              <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">Область применения</p>
            </div>
            <div className="mt-3 space-y-1 text-[13px] text-slate-600">
              <p>Филиал: {rule.branch?.name || 'Все филиалы'}</p>
              <p>Отдел: {rule.department?.name || '-'}</p>
              <p>Смена: {rule.shift?.name || '-'}</p>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 pt-3">
          <button
            type="button"
            className="text-xs font-semibold text-blue-600 hover:text-blue-700"
            onClick={() =>
              onPreview('Детали правила', [
                `Название: ${rule.name || '-'}`,
                `Описание: ${rule.description || '-'}`,
                `Тип опоздания: ${lateTypeLabel}`,
                `Сумма опоздания: ${toDisplayNumber(rule.late_amount)}`,
                `Порог опоздания: ${toDisplayNumber(rule.late_threshold_minutes)} мин`,
                `Ранний уход: ${toDisplayNumber(rule.early_leave_amount)}`,
                `Порог раннего ухода: ${toDisplayNumber(rule.early_leave_threshold_minutes)} мин`,
                ...(Array.isArray(rule.late_tiers) && rule.late_tiers.length > 0
                  ? [
                      'Ступени опоздания:',
                      ...rule.late_tiers.map((tier: any) => `${tier.from_minute}–${tier.to_minute} мин → ${toDisplayNumber(tier.amount)}`),
                    ]
                  : []),
                ...(Array.isArray(rule.early_leave_tiers) && rule.early_leave_tiers.length > 0
                  ? [
                      'Ступени раннего ухода:',
                      ...rule.early_leave_tiers.map((tier: any) => `${tier.from_minute}–${tier.to_minute} мин → ${toDisplayNumber(tier.amount)}`),
                    ]
                  : []),
                `Неявка: ${toDisplayNumber(rule.no_show_amount)}`,
                `Отсутствие: ${toDisplayNumber(rule.absence_amount)}`,
                `Нет выхода: ${toDisplayNumber(rule.no_checkout_amount)}`,
                `Филиал: ${rule.branch?.name || 'Все филиалы'}`,
                `Отдел: ${rule.department?.name || '-'}`,
                `Смена: ${rule.shift?.name || '-'}`,
              ])
            }
          >
            Подробнее
          </button>
          <span className="text-xs text-slate-500">ID правила: {rule.id}</span>
        </div>
      </CardContent>
    </Card>
  );
}

export default function PenaltyRulesPage() {
  const perPage = 10;
  const { can } = useAuth();
  const canManage = can('manage_penalties');
  const [page, setPage] = useState(1);
  const [showForm, setShowForm] = useState(false);
  const [editingRule, setEditingRule] = useState<any>(null);
  const [preview, setPreview] = useState<{ title: string; content: string } | null>(null);
  const [branchFilter, setBranchFilter] = useState('');
  const queryClient = useQueryClient();

  const { data: rulesData, isLoading } = useQuery({
    queryKey: [...QUERY_KEYS.penaltyRules(), branchFilter, page],
    queryFn: () => penaltyRulesAPI.list({ branch_id: branchFilter, page }),
  });

  const { data: branchesData } = useQuery({
    queryKey: QUERY_KEYS.branches(),
    queryFn: () => branchesAPI.list(),
  });

  const { data: shiftsData } = useQuery({
    queryKey: QUERY_KEYS.shifts(),
    queryFn: () => shiftsAPI.list(),
  });

  const createMutation = useMutation({
    mutationFn: (data: any) => penaltyRulesAPI.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.penaltyRules() });
      setShowForm(false);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => penaltyRulesAPI.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.penaltyRules() });
      setEditingRule(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => penaltyRulesAPI.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.penaltyRules() });
    },
  });

  const { items: rules, meta } = extractPaginatedData<any>(rulesData, page, perPage);
  const branches = Array.isArray(branchesData?.data)
    ? branchesData.data
    : branchesData?.data?.data || [];
  const shifts = Array.isArray(shiftsData?.data)
    ? shiftsData.data
    : shiftsData?.data?.data || [];

  const handleDelete = async (id: number) => {
    if (window.confirm('Вы уверены, что хотите удалить это правило?')) {
      await deleteMutation.mutateAsync(id);
    }
  };

  const getLateTypeLabel = (type: string) => {
    return LATE_TYPES.find((t) => t.value === type)?.label || type;
  };

  const openPreview = (title: string, lines: Array<string | number | null | undefined>) => {
    const content = lines
      .filter((line) => line !== null && typeof line !== 'undefined' && String(line).trim() !== '')
      .map((line) => String(line))
      .join('\n');

    setPreview({ title, content: content || '-' });
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  const activeRulesCount = rules.filter((rule: any) => rule.is_active).length;
  const scopedRulesCount = rules.filter((rule: any) => rule.branch_id || rule.department_id || rule.shift_id).length;
  const defaultRulesCount = rules.filter((rule: any) => !rule.branch_id && !rule.department_id && !rule.shift_id).length;

  return (
    <div className="penalty-compact-page space-y-5 page-shell">
      <Card className="overflow-hidden border-slate-200/90 bg-[radial-gradient(circle_at_top_left,_rgba(59,130,246,0.18),_transparent_38%),linear-gradient(135deg,_rgba(255,255,255,0.98),_rgba(239,246,255,0.96))]">
        <CardContent className="p-4 sm:p-5 lg:p-6">
          <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
            <div className="max-w-3xl">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-blue-600/70">Правила штрафов</p>
              <h1 className="mt-2 text-[1.9rem] font-bold tracking-tight text-slate-950 sm:text-[2.25rem]">Правила штрафов</h1>
              <p className="mt-2 max-w-2xl text-[13px] leading-5 text-slate-600 sm:text-sm">
                Этот раздел управляет тем, где и как автоматически начисляются штрафы. Каждое правило отображается со своим охватом, лимитами и порогами.
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <span className="rounded-full border border-white/70 bg-white/80 px-3 py-1 text-xs text-slate-700 shadow-sm">
                  Текущая страница: <strong className="text-slate-900">{rules.length}</strong>
                </span>
                <span className="rounded-full border border-white/70 bg-white/80 px-3 py-1 text-xs text-slate-700 shadow-sm">
                  Активных правил: <strong className="text-slate-900">{activeRulesCount}</strong>
                </span>
                <span className="rounded-full border border-white/70 bg-white/80 px-3 py-1 text-xs text-slate-700 shadow-sm">
                  Правил по умолчанию: <strong className="text-slate-900">{defaultRulesCount}</strong>
                </span>
              </div>
            </div>

            {canManage && (
              <div className="flex w-full flex-col gap-3 sm:flex-row xl:w-auto">
                <Button size="sm" className="shadow-sm shadow-blue-500/20" onClick={() => setShowForm(!showForm)}>
                  <Plus className="mr-2 h-4 w-4" />
                  Добавить правило
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Filter */}
      <Card>
        <CardContent className="space-y-3 py-3.5">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">Фильтр</p>
            <h2 className="mt-1 text-base font-semibold text-slate-900">Просмотр правил по филиалу</h2>
          </div>
          <BranchFilterSelect
            alwaysShow
            className={`${FILTER_SELECT_CLASS} sm:w-72`}
            value={branchFilter}
            onChange={(value) => {
              setPage(1);
              setBranchFilter(value);
            }}
          />
        </CardContent>
      </Card>

      <Modal
        isOpen={showForm || !!editingRule}
        onClose={() => {
          setShowForm(false);
          setEditingRule(null);
        }}
        title={editingRule ? 'Редактировать правило' : 'Новое правило штрафов'}
        maxWidthClass="max-w-5xl"
      >
        <RuleForm
          initialData={editingRule}
          branches={branches}
          shifts={shifts}
          onSubmit={async (data) => {
            if (editingRule) {
              await updateMutation.mutateAsync({ id: editingRule.id, data });
            } else {
              await createMutation.mutateAsync(data);
            }
          }}
          onCancel={() => {
            setShowForm(false);
            setEditingRule(null);
          }}
        />
      </Modal>

      <Modal
        isOpen={!!preview}
        onClose={() => setPreview(null)}
        title={preview?.title || 'Детали'}
        maxWidthClass="max-w-xl"
      >
        <div className="rounded-lg bg-slate-50 p-3">
          <pre className="text-sm leading-6 whitespace-pre-wrap break-words text-slate-700">{preview?.content || '-'}</pre>
        </div>
      </Modal>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <RuleMetricCard
          icon={<Scale className="h-5 w-5 text-blue-600" />}
          iconClassName="bg-blue-100"
          label="Правил на странице"
          value={rules.length}
          helper="Текущая страница пагинации"
        />
        <RuleMetricCard
          icon={<Sparkles className="h-5 w-5 text-emerald-600" />}
          iconClassName="bg-emerald-100"
          label="Активных"
          value={activeRulesCount}
          helper="Работающих автоматических правил"
        />
        <RuleMetricCard
          icon={<MapPinned className="h-5 w-5 text-violet-600" />}
          iconClassName="bg-violet-100"
          label="С областью"
          value={scopedRulesCount}
          helper="Привязаны к филиалу, отделу или смене"
        />
        <RuleMetricCard
          icon={<HelpCircle className="h-5 w-5 text-amber-600" />}
          iconClassName="bg-amber-100"
          label="По умолчанию"
          value={defaultRulesCount}
          helper="Общие резервные правила"
        />
      </div>

      <Card className="border-slate-200/90 bg-white/95">
        <div className="flex flex-col gap-3 border-b border-slate-100 px-3.5 py-3 sm:px-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">Список</p>
            <h2 className="mt-1 text-lg font-semibold tracking-tight text-slate-900">Карточки правил</h2>
            <p className="mt-1 text-[13px] text-slate-500">Вместо таблицы используется адаптивный вид карточек для удобного просмотра.</p>
          </div>
          <span className="rounded-full bg-slate-100 px-3 py-1 text-xs text-slate-700">
            Фильтр: <strong className="text-slate-900">{branchFilter ? branches.find((branch: any) => String(branch.id) === branchFilter)?.name || 'Филиал' : 'Все филиалы'}</strong>
          </span>
        </div>

        <CardContent className="p-4 sm:p-5">
          <div className="grid gap-4 xl:grid-cols-2">
            {rules.map((rule: any) => (
              <RuleListCard
                key={rule.id}
                rule={rule}
                lateTypeLabel={getLateTypeLabel(rule.late_type)}
                canManage={canManage}
                onEdit={setEditingRule}
                onDelete={handleDelete}
                onPreview={openPreview}
              />
            ))}
          </div>
        </CardContent>
      </Card>
      <Pagination meta={meta} onPageChange={setPage} />

      {rules.length === 0 && (
        <div className="text-center py-12">
          <Scale className="w-16 h-16 mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900">Правила штрафов не найдены</h3>
          <p className="text-gray-500">Создайте правила для автоматического расчета штрафов.</p>
        </div>
      )}
    </div>
  );
}

function RuleForm({
  initialData,
  branches,
  shifts,
  onSubmit,
  onCancel,
}: {
  initialData?: any;
  branches: any[];
  shifts: any[];
  onSubmit: (data: any) => Promise<void> | void;
  onCancel: () => void;
}) {
  const toInputValue = (value: any) => {
    if (value === null || typeof value === 'undefined') return '';
    return String(value);
  };

  const toNullableNumber = (value: string) => {
    if (value === '') return null;
    const parsed = Number(value);
    return Number.isNaN(parsed) ? null : parsed;
  };

  const [formData, setFormData] = useState({
    name: initialData?.name || '',
    description: initialData?.description || '',
    late_type: initialData?.late_type || 'fixed',
    late_amount: toInputValue(initialData?.late_amount),
    late_threshold_minutes: toInputValue(initialData?.late_threshold_minutes ?? 0),
    no_show_after_minutes: toInputValue(initialData?.no_show_after_minutes),
    no_show_amount: toInputValue(initialData?.no_show_amount),
    absence_amount: toInputValue(initialData?.absence_amount),
    early_leave_amount: toInputValue(initialData?.early_leave_amount),
    no_checkout_amount: toInputValue(initialData?.no_checkout_amount),
    early_leave_threshold_minutes: toInputValue(initialData?.early_leave_threshold_minutes),
    checkout_grace_minutes: toInputValue(initialData?.checkout_grace_minutes ?? 30),
    max_penalty_per_day: toInputValue(initialData?.max_penalty_per_day),
    max_penalty_per_month: toInputValue(initialData?.max_penalty_per_month),
    priority: toInputValue(initialData?.priority ?? 0),
    branch_id: toInputValue(initialData?.branch_id),
    shift_id: toInputValue(initialData?.shift_id),
    is_active: initialData?.is_active ?? true,
  });

  type PenaltyTier = { from_minute: string; to_minute: string; amount: string };

  const buildInitialTiers = (tiers: any): PenaltyTier[] => (
    Array.isArray(tiers) && tiers.length > 0
      ? tiers.map((tier: any) => ({
          from_minute: toInputValue(tier?.from_minute),
          to_minute: toInputValue(tier?.to_minute),
          amount: toInputValue(tier?.amount),
        }))
      : []
  );

  const [lateTiers, setLateTiers] = useState<PenaltyTier[]>(buildInitialTiers(initialData?.late_tiers));
  const [earlyLeaveTiers, setEarlyLeaveTiers] = useState<PenaltyTier[]>(buildInitialTiers(initialData?.early_leave_tiers));

  const updateTier = (
    setter: React.Dispatch<React.SetStateAction<PenaltyTier[]>>,
    index: number,
    key: keyof PenaltyTier,
    value: string,
  ) => {
    setter((prev) => prev.map((tier, i) => (i === index ? { ...tier, [key]: value } : tier)));
  };

  const addTier = (
    setter: React.Dispatch<React.SetStateAction<PenaltyTier[]>>,
    thresholdMinutes: string,
  ) => {
    setter((prev) => {
      const lastTo = prev.length ? Number(prev[prev.length - 1].to_minute || 0) : Number(thresholdMinutes || 0);
      const from = Number.isFinite(lastTo) ? lastTo + 1 : 0;
      return [...prev, { from_minute: String(from), to_minute: String(from + 4), amount: '' }];
    });
  };

  const removeTier = (setter: React.Dispatch<React.SetStateAction<PenaltyTier[]>>, index: number) => {
    setter((prev) => prev.filter((_, i) => i !== index));
  };

  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showSimpleMode, setShowSimpleMode] = useState(false);

  const applyPreset = (type: 'light' | 'standard' | 'strict') => {
    if (type === 'light') {
      setFormData((prev) => ({
        ...prev,
        late_type: 'fixed',
        late_amount: '15000',
        late_threshold_minutes: '10',
        no_show_after_minutes: '30',
        early_leave_amount: '20000',
        early_leave_threshold_minutes: '15',
        checkout_grace_minutes: '60',
        no_show_amount: '50000',
        absence_amount: '80000',
        no_checkout_amount: '15000',
      }));
      setLateTiers([
        { from_minute: '10', to_minute: '20', amount: '15000' },
        { from_minute: '21', to_minute: '30', amount: '25000' },
      ]);
      setEarlyLeaveTiers([
        { from_minute: '16', to_minute: '30', amount: '20000' },
        { from_minute: '31', to_minute: '60', amount: '30000' },
      ]);
      return;
    }

    if (type === 'strict') {
      setFormData((prev) => ({
        ...prev,
        late_type: 'fixed',
        late_amount: '50000',
        late_threshold_minutes: '5',
        no_show_after_minutes: '30',
        early_leave_amount: '60000',
        early_leave_threshold_minutes: '5',
        checkout_grace_minutes: '15',
        no_show_amount: '200000',
        absence_amount: '150000',
        no_checkout_amount: '50000',
      }));
      setLateTiers([
        { from_minute: '6', to_minute: '10', amount: '20000' },
        { from_minute: '11', to_minute: '15', amount: '40000' },
        { from_minute: '16', to_minute: '20', amount: '60000' },
        { from_minute: '21', to_minute: '25', amount: '80000' },
        { from_minute: '26', to_minute: '30', amount: '100000' },
      ]);
      setEarlyLeaveTiers([
        { from_minute: '6', to_minute: '10', amount: '30000' },
        { from_minute: '11', to_minute: '20', amount: '60000' },
        { from_minute: '21', to_minute: '30', amount: '90000' },
      ]);
      return;
    }

    setFormData((prev) => ({
      ...prev,
      late_type: 'fixed',
      late_amount: '30000',
      late_threshold_minutes: '5',
      no_show_after_minutes: '30',
      early_leave_amount: '40000',
      early_leave_threshold_minutes: '10',
      checkout_grace_minutes: '30',
      no_show_amount: '120000',
      absence_amount: '100000',
      no_checkout_amount: '30000',
    }));
    setLateTiers([
      { from_minute: '6', to_minute: '15', amount: '20000' },
      { from_minute: '16', to_minute: '30', amount: '40000' },
    ]);
    setEarlyLeaveTiers([
      { from_minute: '11', to_minute: '20', amount: '25000' },
      { from_minute: '21', to_minute: '40', amount: '40000' },
    ]);
  };

  const sanitizeTiers = (tiers: PenaltyTier[]) => (
    tiers
      .map((tier) => ({
        from_minute: Number(tier.from_minute),
        to_minute: Number(tier.to_minute),
        amount: Number(tier.amount),
      }))
      .filter((tier) => Number.isFinite(tier.from_minute) && Number.isFinite(tier.to_minute) && Number.isFinite(tier.amount))
      .sort((a, b) => a.from_minute - b.from_minute)
  );

  const validateTierSequence = (
    tiers: Array<{ from_minute: number; to_minute: number; amount: number }>,
    label: string,
    thresholdMinutes: string,
  ) => {
    const expectedStart = Math.max(0, Number(thresholdMinutes || 0)) + 1;

    for (let i = 0; i < tiers.length; i += 1) {
      const tier = tiers[i];
      if (tier.to_minute < tier.from_minute) {
        return `${label} ${i + 1}: значение «По» не может быть меньше «С».`;
      }

      if (i === 0 && tier.from_minute !== expectedStart) {
        return `${label} 1: первая ступень должна начинаться сразу после порога.`;
      }

      if (i > 0 && tier.from_minute !== tiers[i - 1].to_minute + 1) {
        return `${label} ${i + 1}: ступени должны идти подряд без пропусков и пересечений.`;
      }
    }

    return null;
  };

  const handleSubmit = async () => {
    setError('');

    if (!formData.name.trim()) {
      setError('Название правила обязательно.');
      return;
    }

    if (formData.late_amount === '') {
      setError('Поле "Штраф за опоздание" обязательно.');
      return;
    }

    if (Number(formData.late_amount) < 0) {
      setError('Сумма штрафа не может быть отрицательной.');
      return;
    }

    const sanitizedTiers = sanitizeTiers(lateTiers);
    const sanitizedEarlyLeaveTiers = sanitizeTiers(earlyLeaveTiers);

    const lateTierError = validateTierSequence(sanitizedTiers, 'Ступень опоздания', formData.late_threshold_minutes);
    if (lateTierError) {
      setError(lateTierError);
      return;
    }

    const earlyLeaveTierError = validateTierSequence(sanitizedEarlyLeaveTiers, 'Ступень раннего ухода', formData.early_leave_threshold_minutes);
    if (earlyLeaveTierError) {
      setError(earlyLeaveTierError);
      return;
    }

    const noShowAfter = formData.no_show_after_minutes !== ''
      ? Number(formData.no_show_after_minutes)
      : (sanitizedTiers.length > 0 ? sanitizedTiers[sanitizedTiers.length - 1].to_minute : null);

    const payload = {
      name: formData.name.trim(),
      description: formData.description.trim() || null,
      late_type: formData.late_type,
      late_amount: Number(formData.late_amount),
      late_threshold_minutes: Number(formData.late_threshold_minutes || 0),
      late_tiers: sanitizedTiers.length > 0 ? sanitizedTiers : null,
      no_show_after_minutes: noShowAfter,
      no_show_amount: toNullableNumber(formData.no_show_amount),
      absence_amount: toNullableNumber(formData.absence_amount),
      early_leave_amount: toNullableNumber(formData.early_leave_amount),
      early_leave_tiers: sanitizedEarlyLeaveTiers.length > 0 ? sanitizedEarlyLeaveTiers : null,
      no_checkout_amount: toNullableNumber(formData.no_checkout_amount),
      early_leave_threshold_minutes: toNullableNumber(formData.early_leave_threshold_minutes),
      checkout_grace_minutes: Number(formData.checkout_grace_minutes || 30),
      max_penalty_per_day: toNullableNumber(formData.max_penalty_per_day),
      max_penalty_per_month: toNullableNumber(formData.max_penalty_per_month),
      priority: Number(formData.priority || 0),
      branch_id: formData.branch_id ? Number(formData.branch_id) : null,
      shift_id: formData.shift_id ? Number(formData.shift_id) : null,
      is_active: formData.is_active,
    };

    setSubmitting(true);
    try {
      await onSubmit(payload);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Сохранить правило не удалось. Попробуйте еще раз.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-4 pb-1">
      <div className="rounded-xl border border-slate-200 p-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2 text-slate-700">
            <Sparkles className="h-4 w-4" />
            <p className="text-sm font-semibold">Понятный режим настройки</p>
          </div>
          <Button
            type="button"
            size="sm"
            variant={showSimpleMode ? 'default' : 'outline'}
            onClick={() => setShowSimpleMode((prev) => !prev)}
          >
            {showSimpleMode ? 'Скрыть режим' : 'Открыть режим'}
          </Button>
        </div>

        {showSimpleMode && (
          <div className="mt-3 rounded-lg border border-indigo-100 bg-gradient-to-r from-indigo-50 via-blue-50 to-cyan-50 p-4">
            <p className="text-sm text-indigo-800/90">
              Сначала задайте название и базовый штраф за опоздание. Остальные поля можно заполнить позже.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <Button type="button" size="sm" variant="outline" onClick={() => applyPreset('light')}>
                Мягкий шаблон
              </Button>
              <Button type="button" size="sm" variant="outline" onClick={() => applyPreset('standard')}>
                Стандартный шаблон
              </Button>
              <Button type="button" size="sm" variant="outline" onClick={() => applyPreset('strict')}>
                Строгий шаблон
              </Button>
            </div>
          </div>
        )}
      </div>

      <div className="rounded-xl border border-slate-200 p-4">
        <div className="mb-3 flex items-center gap-2 text-slate-900">
          <Scale className="h-4 w-4" />
          <h3 className="text-sm font-semibold">1. Основная информация</h3>
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label className="text-sm text-gray-600 block mb-1">Название правила *</label>
            <Input
              placeholder="Например: Основное правило штрафов"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            />
          </div>
          <div>
            <label className="text-sm text-gray-600 block mb-1">Тип штрафа за опоздание</label>
            <select
              className="w-full rounded-lg border border-slate-200 px-4 py-2"
              value={formData.late_type}
              onChange={(e) => setFormData({ ...formData, late_type: e.target.value })}
            >
              {LATE_TYPES.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="mt-4">
          <label className="text-sm text-gray-600 block mb-1">Описание (необязательно)</label>
          <Input
            placeholder="Например: Правило для дневной смены"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          />
        </div>
      </div>

      {/* ПРИХОД - Check in */}
      <div className="rounded-xl border border-blue-200 bg-blue-50/30 p-4">
        <div className="mb-3 flex items-center gap-2 text-blue-800">
          <Clock className="h-4 w-4" />
          <h3 className="text-sm font-semibold">2. ПРИХОД (Check-in) — Начало рабочего дня</h3>
        </div>
        <div className="mb-3 p-3 bg-white rounded-lg border border-blue-100">
          <p className="text-xs text-blue-700">
            <strong>Пример:</strong> Смена начинается в 09:00. Если «Порог» равен 5 минутам:
            <br />• Приход до 09:05 → <span className="text-green-600 font-medium">Всё в порядке</span>
            <br />• Приход в 09:06 → <span className="text-red-600 font-medium">Штраф за опоздание</span>
          </p>
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm text-gray-700 font-medium">Сумма штрафа за опоздание *</label>
            <Input
              type="number"
              placeholder="например, 30000"
              value={formData.late_amount}
              onChange={(e) => setFormData({ ...formData, late_amount: e.target.value })}
            />
            <p className="mt-1 text-xs text-gray-500">Штраф за опоздание (резервное значение без ступеней)</p>
          </div>
          <div>
            <label className="mb-1 block text-sm text-gray-700 font-medium">Порог опоздания (минут)</label>
            <Input
              type="number"
              placeholder="например, 5"
              value={formData.late_threshold_minutes}
              onChange={(e) => setFormData({ ...formData, late_threshold_minutes: e.target.value })}
            />
            <p className="mt-1 text-xs text-gray-500">До скольки минут опоздание не штрафуется (0 = штраф за любое опоздание)</p>
          </div>
        </div>

        {/* Tiered late penalty ladder */}
        <div className="mt-4 rounded-xl border border-blue-200 bg-white p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <p className="text-sm font-semibold text-blue-900">Ступени опоздания (диапазон → сумма)</p>
              <p className="mt-1 text-xs text-blue-700">
                Пример: смена 09:00, порог 5 мин — 5–10 мин, 10–15 мин, 15–20 мин — разные суммы.
                Приход позже последнего диапазона считается <b>неявкой</b> (а не опозданием).
              </p>
            </div>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => addTier(setLateTiers, formData.late_threshold_minutes)}
            >
              <Plus className="mr-1 h-4 w-4" /> Добавить ступень
            </Button>
          </div>

          {lateTiers.length === 0 && (
            <p className="mt-3 rounded-lg bg-blue-50 px-3 py-2 text-xs text-blue-700">
              Ступени не заданы — используется <b>Сумма штрафа за опоздание</b> выше (обычный режим).
            </p>
          )}

          {lateTiers.map((tier, index) => (
            <div key={index} className="mt-3 grid grid-cols-1 gap-2 rounded-lg border border-slate-200 bg-slate-50 p-3 sm:grid-cols-[1fr_1fr_1fr_auto]">
              <div>
                <label className="text-[11px] font-medium text-slate-500">С (мин)</label>
                <Input
                  type="number"
                  value={tier.from_minute}
                  onChange={(e) => updateTier(setLateTiers, index, 'from_minute', e.target.value)}
                />
              </div>
              <div>
                <label className="text-[11px] font-medium text-slate-500">По (мин)</label>
                <Input
                  type="number"
                  value={tier.to_minute}
                  onChange={(e) => updateTier(setLateTiers, index, 'to_minute', e.target.value)}
                />
              </div>
              <div>
                <label className="text-[11px] font-medium text-slate-500">Сумма (сум)</label>
                <Input
                  type="number"
                  value={tier.amount}
                  onChange={(e) => updateTier(setLateTiers, index, 'amount', e.target.value)}
                />
              </div>
              <div className="flex items-end">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="border-red-200 text-red-600 hover:bg-red-50"
                  onClick={() => removeTier(setLateTiers, index)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}

          <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className="text-xs font-medium text-slate-600">Порог неявки (минут, необязательно)</label>
              <Input
                type="number"
                placeholder={lateTiers.length ? String(lateTiers[lateTiers.length - 1]?.to_minute || '') : 'авто'}
                value={formData.no_show_after_minutes}
                onChange={(e) => setFormData({ ...formData, no_show_after_minutes: e.target.value })}
              />
              <p className="mt-1 text-xs text-slate-500">
                Если не заполнено, используется значение <b>По</b> последней ступени.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* УХОД - Check out */}
      <div className="rounded-xl border border-orange-200 bg-orange-50/30 p-4">
        <div className="mb-3 flex items-center gap-2 text-orange-800">
          <LogOut className="h-4 w-4" />
          <h3 className="text-sm font-semibold">3. УХОД (Check-out) — Конец рабочего дня</h3>
        </div>
        <div className="mb-3 p-3 bg-white rounded-lg border border-orange-100">
          <p className="text-xs text-orange-700">
            <strong>Пример:</strong> Смена заканчивается в 18:00. «Допустимое время ухода» — 30 минут:
            <br />• Face ID до 18:30 → <span className="text-green-600 font-medium">Всё в порядке (поздний уход)</span>
            <br />• Уход в 17:50 (10 мин ранее) при пороге 5 мин → <span className="text-red-600 font-medium">Штраф за ранний уход</span>
            <br />• Нет Face ID после 18:30 → <span className="text-red-600 font-medium">Штраф за отсутствие отметки</span>
          </p>
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm text-gray-700 font-medium">Штраф за ранний уход</label>
            <Input
              type="number"
              placeholder="например, 40000"
              value={formData.early_leave_amount}
              onChange={(e) => setFormData({ ...formData, early_leave_amount: e.target.value })}
            />
            <p className="mt-1 text-xs text-gray-500">Штраф за уход до окончания смены</p>
          </div>
          <div>
            <label className="mb-1 block text-sm text-gray-700 font-medium">Порог раннего ухода (минут)</label>
            <Input
              type="number"
              placeholder="например, 5"
              value={formData.early_leave_threshold_minutes}
              onChange={(e) => setFormData({ ...formData, early_leave_threshold_minutes: e.target.value })}
            />
            <p className="mt-1 text-xs text-gray-500">На сколько минут раньше можно уйти без штрафа (0 = штраф за любой ранний уход)</p>
          </div>
        </div>

        <div className="mt-4 rounded-xl border border-orange-200 bg-white p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <p className="text-sm font-semibold text-orange-900">Ступени раннего ухода (диапазон → сумма)</p>
              <p className="mt-1 text-xs text-orange-700">
                Если настроены ступени, штраф будет зависеть от количества минут раннего ухода.
                После последней ступени используется сумма последней ступени.
              </p>
            </div>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => addTier(setEarlyLeaveTiers, formData.early_leave_threshold_minutes)}
            >
              <Plus className="mr-1 h-4 w-4" /> Добавить ступень
            </Button>
          </div>

          {earlyLeaveTiers.length === 0 && (
            <p className="mt-3 rounded-lg bg-orange-50 px-3 py-2 text-xs text-orange-700">
              Ступени не заданы — используется <b>Штраф за ранний уход</b> выше.
            </p>
          )}

          {earlyLeaveTiers.map((tier, index) => (
            <div key={index} className="mt-3 grid grid-cols-1 gap-2 rounded-lg border border-slate-200 bg-slate-50 p-3 sm:grid-cols-[1fr_1fr_1fr_auto]">
              <div>
                <label className="text-[11px] font-medium text-slate-500">С (мин)</label>
                <Input
                  type="number"
                  value={tier.from_minute}
                  onChange={(e) => updateTier(setEarlyLeaveTiers, index, 'from_minute', e.target.value)}
                />
              </div>
              <div>
                <label className="text-[11px] font-medium text-slate-500">По (мин)</label>
                <Input
                  type="number"
                  value={tier.to_minute}
                  onChange={(e) => updateTier(setEarlyLeaveTiers, index, 'to_minute', e.target.value)}
                />
              </div>
              <div>
                <label className="text-[11px] font-medium text-slate-500">Сумма (сум)</label>
                <Input
                  type="number"
                  value={tier.amount}
                  onChange={(e) => updateTier(setEarlyLeaveTiers, index, 'amount', e.target.value)}
                />
              </div>
              <div className="flex items-end">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="border-red-200 text-red-600 hover:bg-red-50"
                  onClick={() => removeTier(setEarlyLeaveTiers, index)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}

          <p className="mt-3 text-xs text-slate-500">
            Первая ступень должна начинаться сразу после порога раннего ухода.
          </p>
        </div>

        <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm text-gray-700 font-medium">Допустимое время после смены (минут)</label>
            <Input
              type="number"
              placeholder="например, 30"
              value={formData.checkout_grace_minutes}
              onChange={(e) => setFormData({ ...formData, checkout_grace_minutes: e.target.value })}
            />
            <p className="mt-1 text-xs text-gray-500">Сколько минут после смены можно отметиться на Face ID</p>
          </div>
          <div>
            <label className="mb-1 block text-sm text-gray-700 font-medium">Штраф за отсутствие отметки</label>
            <Input
              type="number"
              placeholder="например, 30000"
              value={formData.no_checkout_amount}
              onChange={(e) => setFormData({ ...formData, no_checkout_amount: e.target.value })}
            />
            <p className="mt-1 text-xs text-gray-500">Штраф, если сотрудник не отметился на Face ID при уходе</p>
          </div>
        </div>
      </div>

      {/* НЕЯВКА - No show / Absent */}
      <div className="rounded-xl border border-red-200 bg-red-50/30 p-4">
        <div className="mb-3 flex items-center gap-2 text-red-800">
          <UserX className="h-4 w-4" />
          <h3 className="text-sm font-semibold">4. НЕЯВКА — Отсутствие на работе</h3>
        </div>
        <div className="mb-3 p-3 bg-white rounded-lg border border-red-100">
          <p className="text-xs text-red-700">
            <strong>Неявка (No show):</strong> Сотрудник не пришёл вообще, нет ни одного Face ID
            <br /><strong>Отсутствие (Absent):</strong> Прошедший день — записей Face ID не найдено
          </p>
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm text-gray-700 font-medium">Штраф за неявку (no show)</label>
            <Input
              type="number"
              placeholder="например, 120000"
              value={formData.no_show_amount}
              onChange={(e) => setFormData({ ...formData, no_show_amount: e.target.value })}
            />
            <p className="mt-1 text-xs text-gray-500">Сегодня есть смена, но сотрудник так и не появился</p>
          </div>
          <div>
            <label className="mb-1 block text-sm text-gray-700 font-medium">Штраф за отсутствие (absent)</label>
            <Input
              type="number"
              placeholder="например, 100000"
              value={formData.absence_amount}
              onChange={(e) => setFormData({ ...formData, absence_amount: e.target.value })}
            />
            <p className="mt-1 text-xs text-gray-500">За прошедшие дни — записей нет</p>
          </div>
        </div>
      </div>

      {/* ЛИМИТЫ */}
      <div className="rounded-xl border border-slate-200 p-4">
        <div className="mb-3 flex items-center gap-2 text-slate-900">
          <Ban className="h-4 w-4" />
          <h3 className="text-sm font-semibold">5. Лимиты и приоритет</h3>
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div>
            <label className="mb-1 block text-sm text-gray-600">Дневной лимит</label>
            <Input
              type="number"
              placeholder="необязательно"
              value={formData.max_penalty_per_day}
              onChange={(e) => setFormData({ ...formData, max_penalty_per_day: e.target.value })}
            />
            <p className="mt-1 text-xs text-gray-500">Максимальная сумма штрафов за день</p>
          </div>
          <div>
            <label className="mb-1 block text-sm text-gray-600">Месячный лимит</label>
            <Input
              type="number"
              placeholder="необязательно"
              value={formData.max_penalty_per_month}
              onChange={(e) => setFormData({ ...formData, max_penalty_per_month: e.target.value })}
            />
            <p className="mt-1 text-xs text-gray-500">Максимальная сумма штрафов за месяц</p>
          </div>
          <div>
            <label className="mb-1 block text-sm text-gray-600">Приоритет</label>
            <Input
              type="number"
              placeholder="0"
              value={formData.priority}
              onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
            />
            <p className="mt-1 text-xs text-gray-500">Меньшее число = выше приоритет</p>
          </div>
        </div>
      </div>

      {/* ОБЛАСТЬ ПРИМЕНЕНИЯ */}
      <div className="rounded-xl border border-slate-200 p-4">
        <div className="mb-3 flex items-center gap-2 text-slate-900">
          <MapPinned className="h-4 w-4" />
          <h3 className="text-sm font-semibold">6. Область применения</h3>
        </div>
        <div className="mb-3 p-3 bg-slate-50 rounded-lg border border-slate-100">
          <p className="text-xs text-slate-600">
            Выберите, к какому филиалу и смене применять правило.
            Если оставить пустым, правило применяется ко всем филиалам и сменам.
          </p>
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label className="text-sm text-gray-600 block mb-1">Филиал</label>
            <select
              className="w-full rounded-lg border border-slate-200 px-4 py-2"
              value={formData.branch_id}
              onChange={(e) => setFormData({ ...formData, branch_id: e.target.value })}
            >
              <option value="">Все филиалы</option>
              {branches.map((branch: any) => (
                <option key={branch.id} value={branch.id}>
                  {branch.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-sm text-gray-600 block mb-1">Смена</label>
            <select
              className="w-full rounded-lg border border-slate-200 px-4 py-2"
              value={formData.shift_id}
              onChange={(e) => setFormData({ ...formData, shift_id: e.target.value })}
            >
              <option value="">Все смены</option>
              {shifts.map((shift: any) => (
                <option key={shift.id} value={shift.id}>
                  {shift.name}
                </option>
              ))}
            </select>
          </div>
        </div>
        <label className="mt-4 inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
          <input
            type="checkbox"
            checked={formData.is_active}
            onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
          />
          Правило активно
        </label>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="sticky bottom-0 flex gap-2 bg-white pt-2">
        <Button onClick={handleSubmit} className="flex-1" disabled={submitting}>
          {submitting ? 'Сохранение...' : 'Сохранить правило'}
        </Button>
        <Button onClick={onCancel} variant="outline" className="flex-1" disabled={submitting}>
          Отмена
        </Button>
      </div>
    </div>
  );
}
